import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Quote } from '../types/types';
import { Parallax } from 'react-scroll-parallax';
import ImageCarousel from '../components/ImageCarousel';

type AboutUsSectionType = {
  id: number;
  title: string;
  text: string;
  file: {
    imageUrl: string | null;
  } | null;
};

const Home = () => {
  const [aboutUsSections, setAboutUsSections] = useState<AboutUsSectionType[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isHeaderTransparent, setIsHeaderTransparent] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const resolveImageUrl = (rawUrl: string | null | undefined) => {
    if (!rawUrl) return null;

    // If it's a Nextcloud WebDAV URL, go through the backend proxy
    if (rawUrl.includes('nextcloud.farmtocode.com')) {
      return `${apiBaseUrl}/api/coa/file?url=${encodeURIComponent(rawUrl)}`;
    }

    // Old OCI or any other public URL can be used directly
    return rawUrl;
  };

  useEffect(() => {
    // About Us Sections
    fetch(`${process.env.REACT_APP_API_URL}/api/about-us`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch About Us Sections. Please check your server.');
        return res.json();
      })
      .then((data) => setAboutUsSections(data))
      .catch((err) => {
        console.error('Error fetching sections:', err);
        setError('Error fetching About Us Sections.');
      });

    // Quote
    fetch(`${process.env.REACT_APP_API_URL}/api/quotes`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch quotes. Please check your server.');
        return res.json();
      })
      .then((data) => setQuote(Array.isArray(data) && data.length > 0 ? data[0] : null))
      .catch((err) => {
        console.error('Error fetching quote:', err);
        setError('Error fetching Quote.');
      });
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsHeaderTransparent(window.scrollY <= 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div>
      {/* Transparent Header */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 transition duration-300 ease-in-out ${
          isHeaderTransparent ? 'bg-transparent text-white' : 'bg-gray-900 text-white'
        }`}
      ></div>

      <Layout>
        <div className="min-h-screen bg-black text-white flex flex-col">
          {error && <p className="text-red-500 text-center">{error}</p>}

          {/* Landing Section */}
          <Parallax translateY={[-30, 30]}>
            <section
              className="relative flex items-center min-h-screen bg-cover bg-center"
              style={{ backgroundImage: 'url(/images/BudzBackground.png)' }}
            >
              <div className="text-left p-8 md:p-20 max-w-lg ml-8 md:ml-20">
                <h1 className="text-5xl font-bold text-white mb-4">Welcome to Ridgetop Botanicals</h1>
                <p className="text-2xl text-white mb-8">Where nature meets terpenes</p>
                <a
                  href="#section1"
                  className="px-8 py-4 bg-white text-black text-xl rounded hover:bg-gray-300 transition"
                >
                  Learn More Details
                </a>
              </div>
            </section>
          </Parallax>

          {/* About Us Sections */}
          {aboutUsSections.map((section, index) => (
            <section
              key={section.id}
              id={`section${index + 1}`}
              className={`p-16 text-black flex flex-col md:flex-row ${
                index % 2 === 0 ? '' : 'md:flex-row-reverse'
              } items-stretch`}
            >
              {/* Text column */}
              <div className="md:w-1/2 md:pr-8 flex">
                <Parallax translateY={[-20, 20]} className="w-full">
                  <div
                    className="p-8 rounded-lg shadow-lg relative h-full"
                    style={{
                      backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)), url(/images/${
                        index % 3 === 0 ? 'fracGrey.png' : index % 3 === 1 ? 'fracGrey2.png' : 'topo3.jpg'
                      })`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundBlendMode: 'overlay',
                      backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(240, 240, 240, 0.4)',
                    }}
                  >
                    <h2 className="text-4xl font-bold mb-4">{section.title}</h2>
                    <p className="text-xl mb-8">{section.text}</p>
                    {index < aboutUsSections.length - 1 && (
                      <a
                        href={`#section${index + 2}`}
                        className="px-8 py-4 bg-gradient-to-r from-green-500 to-green-700 text-white font-bold rounded-lg hover:from-green-600 hover:to-green-800 transition duration-300 ease-in-out shadow-lg transform hover:-translate-y-1"
                      >
                        Learn More
                      </a>
                    )}
                  </div>
                </Parallax>
              </div>

              {/* Image column */}
              <div className="md:w-1/2 flex">
                <div className="w-full h-full rounded-lg shadow-lg overflow-hidden">
                  <Parallax translateY={[10, -10]} className="h-full">
                    {(() => {
                      const imageUrl = resolveImageUrl(section.file?.imageUrl);
                      return (
                        imageUrl && (
                          <img
                            src={imageUrl}
                            alt={section.title}
                            className="w-full h-full object-cover"
                          />
                        )
                      );
                    })()}
                  </Parallax>
                </div>
              </div>
            </section>
          ))}

          {/* Quote Section */}
          {quote && (
            <Parallax translateY={[-15, 15]}>
              <section className="relative w-full flex items-center justify-center p-16 bg-black text-white">
                <div className="relative w-full md:w-1/2">
                  <img src={quote.imageUrl} alt="Quote" className="w-full h-auto rounded-lg shadow-lg" />
                  <div
                    className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center text-center p-4 md:p-8"
                    style={{ color: quote.textColor }}
                  >
                    <blockquote className="mb-4 font-bold text-xl md:text-2xl lg:text-3xl xl:text-4xl">
                      &ldquo;{quote.text}&rdquo;
                    </blockquote>
                    <footer className="font-semibold text-sm md:text-lg lg:text-xl xl:text-2xl">
                      - {quote.author || 'Anonymous'}
                    </footer>
                  </div>
                </div>
              </section>
            </Parallax>
          )}

          {/* Bottom Carousel */}
          <ImageCarousel />
        </div>
      </Layout>
    </div>
  );
};

export default Home;
